import React, { useState, useEffect } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { ShoppingCart, User, Menu, X, Search, MapPin } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";

const NAV_ITEMS = [
  { to: "/paquetes", label: "Paquetes" },
  { to: "/parrilladas", label: "Parrilladas" },
  { to: "/arre-box", label: "Arre Box" },
  { to: "/arma-tu-paquete", label: "Arma tu combo" },
  { to: "/catalogo/cortes", label: "Cortes" },
  { to: "/blog", label: "Blog" },
  { to: "/regalo", label: "Regalo" },
  { to: "/empresas", label: "Empresas" },
];

export default function Navigation() {
  const { count, setOpen: setCartOpen } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [coverage, setCoverage] = useState(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    try {
      const c = localStorage.getItem("arre_coverage");
      if (c) setCoverage(JSON.parse(c));
    } catch {}
    const handler = () => {
      try {
        const c = localStorage.getItem("arre_coverage");
        if (c) setCoverage(JSON.parse(c));
      } catch {}
    };
    window.addEventListener("arre_coverage_updated", handler);
    return () => window.removeEventListener("arre_coverage_updated", handler);
  }, []);

  return (
    <>
      {/* Top ribbon */}
      <div className="bg-arre-red text-white text-[11px] tracking-[0.25em] uppercase font-medium overflow-hidden" data-testid="top-ribbon">
        <div className="flex animate-marquee marquee-track py-2">
          {Array(2).fill(0).map((_, idx) => (
            <div key={idx} className="flex items-center gap-12 pr-12 whitespace-nowrap">
              <span>100% Carne Sonorense</span>
              <span>•</span>
              <span>Envío Mismo Día en CDMX / EDOMEX</span>
              <span>•</span>
              <span>Cobertura Nacional</span>
              <span>•</span>
              <span>¡Qué onda, compare!</span>
              <span>•</span>
              <span>Boutique de Carnes Premium</span>
              <span>•</span>
            </div>
          ))}
        </div>
      </div>

      <header
        className={`sticky top-0 z-40 transition-all duration-300 ${
          scrolled ? "backdrop-blur-xl bg-arre-black/85 border-b border-arre-border" : "bg-arre-black/60"
        }`}
        data-testid="main-nav"
      >
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 h-20 flex items-center justify-between gap-6">
          {/* Mobile menu */}
          <button
            className="lg:hidden text-white"
            onClick={() => setMobileOpen(true)}
            aria-label="Menu"
            data-testid="mobile-menu-btn"
          >
            <Menu className="w-6 h-6" strokeWidth={1.5} />
          </button>

          {/* Logo */}
          <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
            <img src="/logo.png" alt="Arre Sonora" className="h-12 w-12 object-contain" />
            <div className="hidden sm:block leading-none">
              <div className="font-display text-2xl tracking-wider text-white">¡ARRE!</div>
              <div className="font-display text-2xl tracking-wider text-arre-red -mt-1">SONORA</div>
            </div>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-8">
            {NAV_ITEMS.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  `font-display text-lg tracking-wider uppercase transition-colors ${
                    isActive ? "text-arre-red" : "text-white/80 hover:text-white"
                  }`
                }
                data-testid={`nav-${item.label.toLowerCase()}`}
              >
                {item.label}
              </NavLink>
            ))}
          </nav>

          {/* Right cluster */}
          <div className="flex items-center gap-4">
            {coverage?.covered && (
              <button
                className="hidden md:flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-white/70 hover:text-white border-l border-arre-border pl-4"
                onClick={() => window.dispatchEvent(new Event("open_zip_popup"))}
                data-testid="coverage-indicator"
              >
                <MapPin className="w-4 h-4" strokeWidth={1.5} />
                <span>CP {coverage.zip}</span>
                {coverage.same_day && <span className="text-arre-red font-bold">• HOY</span>}
              </button>
            )}
            <button
              onClick={() => navigate(user ? "/cuenta" : "/login")}
              className="text-white/80 hover:text-white transition-colors"
              aria-label="Cuenta"
              data-testid="account-btn"
            >
              <User className="w-5 h-5" strokeWidth={1.5} />
            </button>
            <button
              onClick={() => setCartOpen(true)}
              className="relative text-white/80 hover:text-white transition-colors"
              aria-label="Carrito"
              data-testid="cart-btn"
            >
              <ShoppingCart className="w-5 h-5" strokeWidth={1.5} />
              {count > 0 && (
                <span
                  className="absolute -top-2 -right-2 bg-arre-red text-white text-[10px] font-bold w-4 h-4 flex items-center justify-center rounded-full animate-pulse-red"
                  data-testid="cart-count"
                >
                  {count}
                </span>
              )}
            </button>
          </div>
        </div>
      </header>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="fixed inset-0 z-50 lg:hidden" data-testid="mobile-drawer">
          <div className="absolute inset-0 bg-black/70" onClick={() => setMobileOpen(false)} />
          <div className="absolute left-0 top-0 bottom-0 w-[85%] max-w-sm bg-arre-black border-r border-arre-border p-6 overflow-y-auto animate-slide-in-right">
            <div className="flex items-center justify-between mb-10">
              <img src="/logo.png" alt="Arre Sonora" className="h-12" />
              <button onClick={() => setMobileOpen(false)} className="text-white" data-testid="mobile-close">
                <X className="w-6 h-6" />
              </button>
            </div>
            <nav className="flex flex-col gap-5">
              {NAV_ITEMS.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  onClick={() => setMobileOpen(false)}
                  className="font-display text-3xl tracking-wider uppercase text-white hover:text-arre-red transition-colors"
                >
                  {item.label}
                </NavLink>
              ))}
            </nav>
            <div className="mt-10 pt-6 border-t border-arre-border">
              <button
                onClick={() => {
                  setMobileOpen(false);
                  navigate(user ? "/cuenta" : "/login");
                }}
                className="font-display text-xl text-arre-red tracking-wider"
                data-testid="mobile-account-link"
              >
                {user ? `Hola, ${user.name.split(" ")[0]}` : "Iniciar sesión"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
